import { hasDefinedValue, isDefined } from '@sgme/fp';
import { BasketResults } from 'components/common-form/sidePanel/advisory/basket-optimizer/basket-result';
import { BasketOptimizerProgressionBar } from 'components/common-form/sidePanel/advisory/basket-optimizer/progression-bar';
import { BasketOptimizerUnderlyingsErrors } from 'components/common-form/sidePanel/advisory/basket-optimizer/underlying-errors';
import { UnderlyingsSelector } from 'components/common-form/sidePanel/advisory/basket-optimizer/underlyings-selector';
import { BasketOptimizerWorkflowToggle } from 'components/common-form/sidePanel/advisory/basket-optimizer/workflow-toggle';
import { FormattedMessage } from 'components/shared/Intl';
import { LabelWithIconTooltip } from 'components/shared/LabelWithIconToolTip/LabelWithIconTooltip';
import { LightAlert } from 'components/shared/LightAlert';
import { Percent } from 'components/shared/Percent/Percent';
import { SwitchWithLabel } from 'components/shared/Switch/Switch';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import type { IntlKey } from 'locales';
import { UnderlyingsFieldMetadata } from 'models/forms/fields/UnderlyingsFieldMetadata';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import {
  basketOptimizerCouponFromChanged,
  basketOptimizerCouponToChanged,
  basketOptimizerIsMutuallyExclusiveToggled,
  basketOptimizerTargetBasketSizeChanged,
} from 'state/advisory/basket-optimizer/actions';
import {
  getCouponFrom,
  getCouponTo,
  getErrors,
  getIsMutuallyExclusive,
  getTargetBasketSize,
  getUnderlyingTags,
} from 'state/advisory/basket-optimizer/selectors';
import { getFieldsMetadata } from 'state/forms/metadata/selectors';
import { getQuoteSolvingModeValue, isQuoteConsistentForAdvisory } from 'state/selectors/common/commonForm';
import { getTabQuoteWorkspaceType } from 'state/workspaces/workspaces/workspaces.selector';

const EMPTY_FIELDS_METADATA = {};
const useOptimizerForm = (tabId: string) => {
  const dispatch = useThunkDispatch();
  const solvingMode = useAppSelector(s => getQuoteSolvingModeValue(s, tabId));
  const isCouponVisible = solvingMode === 'CouponValue' || solvingMode === 'ConditionalAndRecallCoupons';
  const workspaceType = useAppSelector(s => getTabQuoteWorkspaceType(s, tabId));
  const isQuoteConsistent = useAppSelector(s =>
    isDefined(workspaceType) ? isQuoteConsistentForAdvisory(s, workspaceType, tabId) : undefined,
  );

  const underlyingFieldMetadatas = useAppSelector(s =>
    Object.values(getFieldsMetadata(s, 'static', tabId) ?? EMPTY_FIELDS_METADATA)
      .map(f => f as UnderlyingsFieldMetadata)
      .filter(hasDefinedValue('underlyingBlock')),
  );

  return {
    isQuoteConsistent,
    isCouponVisible,
    underlyingTags: useAppSelector(s => getUnderlyingTags(s, tabId) || undefined),
    errorMessage: useAppSelector(s => getErrors(s, tabId)),
    targetBasketSize: useAppSelector(s => getTargetBasketSize(s, tabId)),
    couponFrom: useAppSelector(s => getCouponFrom(s, tabId)),
    couponTo: useAppSelector(s => getCouponTo(s, tabId)),
    isMutuallyExclusive: useAppSelector(s => getIsMutuallyExclusive(s, tabId)!),
    onIsMutuallyExclusiveToggled() {
      dispatch(basketOptimizerIsMutuallyExclusiveToggled(tabId));
    },
    onCouponFromChanged(value?: number) {
      dispatch(basketOptimizerCouponFromChanged(tabId, value));
    },
    onTargetBasketSizeChanged(value?: number) {
      dispatch(basketOptimizerTargetBasketSizeChanged(tabId, value));
    },
    onCouponToChanged(value?: number) {
      dispatch(basketOptimizerCouponToChanged(tabId, value));
    },
    underlyingFieldMetadatas,
  };
};

type Props = { tabId: string };
export function BasketOptimizer({ tabId }: Props) {
  const intl = useIntl();

  const {
    errorMessage,
    underlyingTags,
    isCouponVisible,
    isMutuallyExclusive,
    onIsMutuallyExclusiveToggled,
    onTargetBasketSizeChanged,
    couponFrom,
    couponTo,
    isQuoteConsistent,
    onCouponFromChanged,
    onCouponToChanged,
    targetBasketSize,
    underlyingFieldMetadatas,
  } = useOptimizerForm(tabId);

  const onBasketSizeChange = useCallback(
    (evt: any) => {
      const value = Number.parseInt(evt.target.value, 10);
      onTargetBasketSizeChanged(Number.isFinite(value) ? value : undefined);
    },
    [onTargetBasketSizeChanged],
  );

  return (
    <>
      <div className="card" data-e2e="basket-optimizer-panel">
        <form>
          <div>
            <div className="bg-light bg-opacity-10 p-4">
              {errorMessage && (
                <ul className="list-group mb-3">
                  <li className="list-group-item p-0 border-0">
                    <LightAlert severity="danger" e2e="basket-optimizer-error" className="mx-0">
                      <FormattedMessage id={errorMessage as IntlKey} />
                    </LightAlert>
                  </li>
                </ul>
              )}
              <div className="form-group mb-0">
                <SwitchWithLabel
                  id="mutuallyExclusive"
                  label={intl.formatMessage({ id: 'basketOptimizer.mutuallyExclusive' })}
                  onToggleSwitch={onIsMutuallyExclusiveToggled}
                  isDisabled={false}
                  value={isMutuallyExclusive}
                  colSize={7}
                  isAlignedLeft
                  icon="info_outline"
                  tooltip={intl.formatMessage({ id: 'basketOptimizer.mutuallyExclusive.tooltip' })}
                />
              </div>
              {isCouponVisible && (
                <div className="form-group">
                  <LabelWithIconTooltip
                    tooltip={intl.formatMessage({ id: 'basketOptimizer.coupon.tooltip' })}
                    label="basketOptimizer.coupon"
                    defaultLabel="Coupon"
                  />
                  <div className="row d-flex flex-row">
                    <div className="col-sm-6">
                      <div className="input-group">
                        <span className="input-group-text">
                          <FormattedMessage id="basketOptimizer.couponFrom" />
                        </span>
                        <Percent
                          id="couponFrom"
                          onChange={onCouponFromChanged}
                          value={couponFrom}
                          e2e="couponFrom"
                          precision={4}
                        />
                        <span className="unit input-group-text" data-e2e="unit">
                          %
                        </span>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="input-group">
                        <span className="input-group-text">
                          <FormattedMessage id="basketOptimizer.couponTo" />
                        </span>
                        <Percent
                          id="couponTo"
                          onChange={onCouponToChanged}
                          value={couponTo}
                          e2e="couponTo"
                          precision={4}
                        />
                        <span className="unit input-group-text" data-e2e="unit">
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <BasketOptimizerUnderlyingsErrors tabId={tabId} />
              <div className="form-group row pt-2">
                <label className="col-sm-6 text-truncate text-secondary text-nowrap my-auto" htmlFor="basket-size">
                  <FormattedMessage id="basketOptimizer.basketSize" />
                </label>
                <div className="col-sm-6">
                  <select
                    id="basket-size"
                    className="form-select"
                    onChange={onBasketSizeChange}
                    value={targetBasketSize}
                  >
                    {underlyingTags &&
                      underlyingTags.map((_, index) =>
                        index === 0 ? (
                          <option key={index} value=""></option>
                        ) : (
                          <option key={index} value={index}>
                            {index}
                          </option>
                        ),
                      )}
                  </select>
                </div>
              </div>
              <div className="form-group mb-0">
                <label className="text-secondary">
                  <FormattedMessage id="basketOptimizer.underlyings.underlyingsToOptimize" />
                </label>
                {underlyingFieldMetadatas.map((f, index) => (
                  <UnderlyingsSelector tabId={tabId} fieldId={f.id} key={index} />
                ))}
              </div>
            </div>
          </div>
          <BasketOptimizerWorkflowToggle tabId={tabId} />
          {!isQuoteConsistent && (
            <LightAlert severity="info">
              <FormattedMessage id="basketOptimizer.reprice" />
            </LightAlert>
          )}
        </form>
      </div>
      <BasketOptimizerProgressionBar tabId={tabId} />
      <BasketResults tabId={tabId} />
    </>
  );
}
